import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, Subscriber, catchError, finalize, from, map, of, switchMap, throwError } from 'rxjs';
// import { environment } from '../../../environments/environment';
import { SpinnerService } from './spinner.service';
import { API_CONFIG } from '../tokens/config.tokens';
import { ToastrService } from 'ngx-toastr';
import { ApiResponseOptions } from '../models/api-response.models';
import { ThemeService } from 'apps/cinari-erp-client/src/app/shared/services/theme.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from '@openng/optimus-ui/api';

@Injectable({
  providedIn: 'root'
})
export class ApiCallingV2Service {
  baseUrl: any;
  #apiConfig = inject(API_CONFIG);
  #toastr = inject(ToastrService);
  #themeService = inject(ThemeService);
  #messageService = inject(MessageService);

  currentTheme = toSignal<('legacy' | 'primeng') | null>(this.#themeService.currentTheme$, { initialValue: null });
  constructor(
    private _httpClient: HttpClient,
    private _spinnerService: SpinnerService
  ) {
    this.baseUrl = this.#apiConfig.baseUrl;
  }

  responseEnum = {
    0: 'Unable to load data. Please retry ',
    200: 'Success',
    400: 'Error',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Already Exists',
    500: 'Internal Server Error'
  };

  getData<T>(urlPath: string, config: ApiResponseOptions = { showLoader: true, showMessage: true }): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.get<any>(`${urlPath}`, config.options).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');

        return throwError(() => error);
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  showMessage(message: string, type: 'success' | 'error' | 'warning' | 'info') {
    if (!message) return;

    // check if theme is legacy-theme
    if (this.currentTheme() === 'legacy') {
      this.#toastr[type](message);
    }
    if (this.currentTheme() === 'primeng') {
      let severity: string = type;

      if (type === 'warning') {
        severity = 'warn';
      }
      this.#messageService.add({ severity: severity, summary: message });
    }
  }

  postData<T>(urlPath: string, config: ApiResponseOptions, data?: any): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.post<any>(`${urlPath}`, data, config.options).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');

        return throwError(() => error);
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  patchData<T>(urlPath: string, data: any, config: ApiResponseOptions): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.patch<any>(`${urlPath}`, data, config.options).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');
        return throwError(() => error);
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  putData<T>(
    urlPath: string,
    data?: any,
    config: ApiResponseOptions = { showLoader: true, showMessage: true }
  ): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.put<any>(`${urlPath}`, data).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) {
          this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');
        }
        return throwError(() => error);
      })
    );
  }

  deleteData<T>(urlPath: string, config: ApiResponseOptions): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.delete<any>(`${urlPath}`).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) {
          this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  deleteDataWithBody<T>(urlPath: string, data: any, config: ApiResponseOptions): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient.delete<any>(`${urlPath}`, { body: data }).pipe(
      map(response => {
        if (config.showMessage ?? true) {
          this.showMessage(
            config.message ??
              response?.message ??
              this.responseEnum[response?.status as keyof typeof this.responseEnum],
            'success'
          );
        }
        return response?.data;
      }),

      catchError((error: HttpErrorResponse) => {
        if (config.showError ?? true) {
          this.showMessage(this.getErrorMessage(error, config.errorMessage), 'error');
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  fileUpload<T>(urlPath: string, data: any, config: ApiResponseOptions): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient
      .post<any>(`${urlPath}`, data, {
        reportProgress: true,
        observe: 'events'
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (config.showError ?? true) {
            this.showMessage(this.getErrorMessage(error), 'error');
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (config.showLoader ?? true) {
            this._spinnerService.handleSpinnerStop(config.buttonId);
          }
        })
      );
  }

  awsFileUpload<T>(url: string, data: any, config: ApiResponseOptions): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    // Only include x-amz-acl header if the presigned URL was signed with it
    const signedHeadersParam = url.match(/X-Amz-SignedHeaders=([^&]+)/);
    const signedHeaders = signedHeadersParam ? decodeURIComponent(signedHeadersParam[1]).toLowerCase() : '';
    const requiresAclHeader = signedHeaders.includes('x-amz-acl');
    return this._httpClient
      .put<any>(url, data, {
        reportProgress: true,
        observe: 'events',
        headers: {
          // Required because presigned URL was created with x-amz-acl header for public uploads
          ...(requiresAclHeader ? { 'x-amz-acl': 'public-read' } : {}),
          ...(data?.type ? { 'Content-Type': data.type } : {})
        }
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (config.showError ?? true) {
            this.showMessage(this.getErrorMessage(error), 'error');
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (config.showLoader ?? true) {
            this._spinnerService.handleSpinnerStop(config.buttonId);
          }
        })
      );
  }

  awsFileUploadWithFetch<T>(
    url: string,
    data: any,
    config: ApiResponseOptions = { showLoader: true, showMessage: false, showError: true }
  ): Observable<any> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    const signedHeadersParam = url.match(/X-Amz-SignedHeaders=([^&]+)/);
    const signedHeaders = signedHeadersParam ? decodeURIComponent(signedHeadersParam[1]).toLowerCase() : '';
    const requiresAclHeader = signedHeaders.includes('x-amz-acl');

    const headers: HeadersInit = {
      'Content-Type': data?.type ? data.type : 'application/octet-stream',
      ...(requiresAclHeader ? { 'x-amz-acl': 'public-read' } : {})
    };

    return new Observable((observer: Subscriber<Response>) => {
      fetch(url, {
        method: 'PUT',
        headers,
        body: data
      })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
          }
          observer.next(response);
          observer.complete();
        })
        .catch(error => {
          if (config.showError ?? true) {
            const msg = error?.message ?? this.responseEnum[500 as keyof typeof this.responseEnum];
            this.showMessage(msg, 'error');
          }
          observer.error(error);
        });
    }).pipe(
      finalize(() => {
        if (config.showLoader ?? true) {
          this._spinnerService.handleSpinnerStop(config.buttonId);
        }
      })
    );
  }

  downloadFile(
    urlPath: string,
    body: any,
    config: ApiResponseOptions,
    filenameDownloadName?: string
  ): Observable<void> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient
      .post(`${urlPath}`, body, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }),
        catchError((error: HttpErrorResponse) => {
          if (config.showError ?? true) {
            this.showMessage(this.getErrorMessage(error), 'error');
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (config.showLoader ?? true) {
            this._spinnerService.handleSpinnerStop(config.buttonId);
          }
        })
      );
  }

  downloadReportFile(
    urlPath: string,
    config: ApiResponseOptions,
    body?: any,
    filenameDownloadName?: string
  ): Observable<void> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient
      .get(`${urlPath}${body ? `?${body}` : ''}`, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          // Create download link and trigger
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }),
        catchError((error: HttpErrorResponse) => {
          const errBody$ =
            error.error instanceof Blob
              ? from(
                  (error.error as Blob).text().then(t => {
                    try {
                      return JSON.parse(t) as object;
                    } catch {
                      return { message: error.statusText || 'Download failed' };
                    }
                  })
                )
              : from(Promise.resolve(error.error ?? { message: error.statusText || 'Some error has occurred' }));
          return errBody$.pipe(
            switchMap(errBody => {
              if (config.showError ?? true) {
                this.showMessage(this.getErrorMessage(error), 'error');
              }
              return throwError(() => errBody);
            })
          );
        }),
        finalize(() => {
          if (config.showLoader ?? true) {
            this._spinnerService.handleSpinnerStop(config.buttonId);
          }
        })
      );
  }

  downloadReportFileWithBody(
    urlPath: string,
    body: any,
    config: ApiResponseOptions,
    filenameDownloadName?: string
  ): Observable<void> {
    if (config.showLoader ?? true) {
      this._spinnerService.handleSpinnerStart(config.buttonId);
    }

    return this._httpClient
      .post(`${urlPath}`, body, {
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map(response => {
          // Create blob from response
          const blob = new Blob([response.body!], {
            type: response.headers.get('Content-Type') || 'application/octet-stream'
          });

          // Get filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          const filename = contentDisposition
            ? contentDisposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
            : 'download';

          // Create download link and trigger
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filenameDownloadName ? filenameDownloadName : filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }),
        catchError((error: HttpErrorResponse) => {
          if (config.showError ?? true) {
            this.showMessage(this.getErrorMessage(error), 'error');
          }
          return throwError(() => error);
        }),
        finalize(() => {
          if (config.showLoader ?? true) {
            this._spinnerService.handleSpinnerStop(config.buttonId);
          }
        })
      );
  }

  async getBlobFromFileUrl(fileUrl: string): Promise<string> {
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  private getErrorMessage(error: HttpErrorResponse, customErrorMessage?: string): string {
    if (customErrorMessage) return customErrorMessage;
    const fallback = 'Error occurred while processing the request. Unable to set error message';
    let errorMessage = fallback;
    if (error.error?.errors && Array.isArray(error.error?.errors) && error.error?.errors.length > 0) {
      errorMessage = error.error?.errors.join(', ');
    } else if (error.error?.errors && typeof error.error?.errors === 'string') {
      errorMessage = error.error?.errors;
    } else {
      errorMessage =
        error.error?.message ?? this.responseEnum[error.status as keyof typeof this.responseEnum] ?? fallback;
    }
    return errorMessage || fallback;
  }
}
